import React, { useCallback, useEffect, useState, useRef, lazy } from 'react';
import { X } from 'lucide-react';
import { trackAddToCart } from '../hooks/useMetaPixel';
import { useCountdown } from '../hooks/useCountdown';
export function ExitIntentPopup() {
  const [isVisible, setIsVisible] = useState(false);
  const [hasTriggered, setHasTriggered] = useState(false);
  const {
    formattedTime,
    isExpired
  } = useCountdown(undefined, 600);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const pollRef = useRef<NodeJS.Timeout | null>(null);
  const setupObserver = useCallback((element: Element) => {
    if (observerRef.current) return; // already set up
    observerRef.current = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Start 2-second timer when pricing section is visible
          if (!timerRef.current) {
            timerRef.current = setTimeout(() => {
              setIsVisible(true);
              setHasTriggered(true);
            }, 8000);
          }
        } else {
          // Clear timer if user scrolls away
          if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
          }
        }
      });
    }, {
      threshold: 0.1
    });
    observerRef.current.observe(element);
  }, []);
  useEffect(() => {
    if (hasTriggered) return;
    // Poll for the element since it's lazy-loaded
    const tryFind = () => {
      const el = document.getElementById('get-the-method');
      if (el) {
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
        setupObserver(el);
      }
    };
    // Try immediately
    tryFind();
    // If not found, poll every 500ms
    if (!observerRef.current) {
      pollRef.current = setInterval(tryFind, 500);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (observerRef.current) observerRef.current.disconnect();
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [hasTriggered, setupObserver]);
  const close = () => setIsVisible(false);
  if (!isVisible) return null;
  return <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={close} style={{
    animation: 'fadeIn 0.3s ease-out'
  }} data-id="element-117">
      <div className="bg-[#FAFAF7] w-full max-w-[420px] overflow-hidden flex flex-col border border-[#E5E5E0] shadow-2xl" onClick={e => e.stopPropagation()} style={{
      animation: 'fadeIn 0.4s ease-out'
    }} data-id="element-118">
        {/* Top accent bar */}
        <div className="h-1 bg-[#8B2500] w-full" data-id="element-119" />

        {/* Close button */}
        <div className="flex justify-end p-3 pb-0" data-id="element-120">
          <button onClick={close} className="p-1 hover:bg-[#E5E5E0] rounded-sm transition-colors" data-id="element-121">
            <X size={18} className="text-[#888]" data-id="element-122" />
          </button>
        </div>

        {/* Content */}
        <div className="px-8 pb-8 pt-2 text-center" data-id="element-123">
          <p className="text-[10px] uppercase tracking-widest text-[#8B2500] font-sans font-bold mb-3" data-id="element-124">
            Before you go
          </p>

          <h3 className="text-[22px] font-bold text-[#1a1a1a] font-serif leading-tight mb-3" data-id="element-125">
            Don't leave without your reader discount
          </h3>

          <p className="text-[14px] text-[#4a4a4a] font-serif leading-relaxed mb-5" data-id="element-126">
            The Knee Geometry Method is available at{' '}
            <strong className="text-[#1a1a1a]" data-id="element-127">65% off</strong> right now. This
            introductory price won't last — once it's gone, the guide returns to
            $19.99.
          </p>

          {/* Price */}
          <div className="flex items-baseline justify-center gap-3 mb-4" data-id="element-128">
            <span className="text-[14px] text-[#888] line-through font-sans" data-id="element-129">
              $19.99
            </span>
            <span className="text-[28px] font-bold text-[#1a1a1a] font-sans" data-id="element-130">
              $6.99
            </span>
          </div>

          {/* Timer */}
          {!isExpired && <div className="inline-flex items-center gap-2 bg-[#1a1a1a]/5 px-3 py-1.5 rounded-sm mb-5" data-id="element-131">
              <span className="text-[10px] text-[#666] font-sans uppercase tracking-wider" data-id="element-132">
                Offer expires in
              </span>
              <span className="text-[13px] font-bold text-[#8B2500] font-sans tabular-nums" data-id="element-133">
                {formattedTime}
              </span>
            </div>}

          {/* CTA */}
          <a href="https://oriammarketing.com/cart/45536364036294:1" onClick={() => trackAddToCart()} className="block w-full bg-[#8B2500] text-white font-sans font-bold text-[14px] uppercase tracking-wide px-6 py-3.5 rounded-sm hover:bg-[#6d1d00] transition-colors text-center no-underline mb-3" data-id="element-134">
            Get the Guide — $6.99
          </a>

          <button onClick={close} className="text-[12px] text-[#888] font-sans hover:text-[#4a4a4a] transition-colors" data-id="element-135">
            No thanks, I'll pass
          </button>
        </div>
      </div>
    </div>;
}